import { defineStore } from 'pinia'
import { DocumentApi } from '../test-api/index.ts'
import useBcSearchStore from './bcSearch'
import assign from 'lodash/assign'
import findIndex from 'lodash/findIndex'
import PromiseQueue from '../promiseQueue'
import handleApiError from '../utils/handleApiError'

const documentApi = new DocumentApi({}, '/api/v2')
const bcSearchStore = useBcSearchStore()

// save promise queue -> save api call queue
export const promiseQueue = new PromiseQueue({
  concurrent: 1,
  interval: 100,
  start: true
})


let documentChangeRevisionCache = null
let checkAVInt = null
let checkAVType = null // fileFilesCheckDocument || fileFilesCheckSharedDocument || fileFilesCheckDocumentRev
const filesToAVTest = new Map()

const flatten = {
  flattenDocument: new Map(),
  flattenDocumentByFromDataRevId: new Map()
}

const buildFlatten = function (children) {
  children.forEach((aData) => {
    if (aData.id) {
      flatten.flattenDocument.set(aData.id, aData)
    }

    if (aData.from__bc_document_data_rev_id) {
      flatten.flattenDocumentByFromDataRevId.set(aData.from__bc_document_data_rev_id, aData)
    }

    if (aData.children) {
      buildFlatten(aData.children)
    }
  })
}

const resetFlatten = function (document) {
  flatten.flattenDocument.clear()
  flatten.flattenDocumentByFromDataRevId.clear()
  buildFlatten(document.children)
}

const updateRootRanksRecursive = function (children, rank = 0) {
  let i = 0
  const len = children.length;
  while (i < len) {
    if (children[i].root_rank !== rank) {
      children[i].root_rank = rank++
    
    } else {
      ++rank
    }

    if (children[i].children && children[i].children.length) {
      rank = updateRootRanksRecursive(children[i].children, rank)
    }

    i++
  }

  return rank
}



const state = () => ({
  loading: true, // is document loading now?
  edit: false, // is document editing now?
  willLoadInEdit: false, // will load document in edit mode?
  isNewDocument: false, // is this document new? Auto focus header
  willChangeRevision: false, // will document change revision?
  document: {},
  documentRev: null, // is document loaded in some revision?
  revisionsList: [],
  recentlyViewed: [],
})

// functionName: (state, getters, rootState) => {}
const getters = {
  search() { // search in document text
    return bcSearchStore.query
  },
  revisions() {
    return this.revisionsList.map(revItem => {
      if (revItem.id !== this.document.published__bc_document_rev_id || this.edit === true) {
        return revItem
      }
      
      const rev = { ...revItem }

      let pattern = new RegExp('/' + this.document.published__bc_document_rev_id + '$')
      rev.url = rev.url.replace(pattern, '')

      return rev
    })
  }
}

// functionName ({ commit, state, dispatch }, param, ...) {}
const actions = {
  reset() {
    this.edit = false
    this.willLoadInEdit = false
    this.isNewDocument = false
    this.willChangeRevision = false
    this.document = {}
    this.documentRev = null
    this.revisionsList = []
    flatten.flattenDocument.clear()
    flatten.flattenDocumentByFromDataRevId.clear()
  },
  getFlattenDocument() {
    return flatten.flattenDocument
  },
  getFlattenDocumentByFromDataRevId() {
    return flatten.flattenDocumentByFromDataRevId
  },
  removeFromFlatten(element) {
    flatten.flattenDocument.delete(element.id)
    flatten.flattenDocumentByFromDataRevId.delete(element.from__bc_document_data_rev_id)
    
    ;(element.children || []).forEach((child) => this.removeFromFlatten(child))
  },
  addToFlatten(element) {
    if (element.id) {
      flatten.flattenDocument.set(element.id, element)
    }

    if (element.from__bc_document_data_rev_id) {
      flatten.flattenDocumentByFromDataRevId.set(element.from__bc_document_data_rev_id, element)
    }

    if (element.children) {
      buildFlatten(element.children)
    }
  },
  setDocument(document) {
    this.document = document
    resetFlatten(this.document)
  },
  // flattens document and it's children to one level map
  flattenDocumentRecursion (children, map, col) {
    children.forEach((aData) => {
      if (aData[col]) {
        map.set(aData[col], aData)
      }

      if (aData.children) {
        this.flattenDocumentRecursion(aData.children, map, col)
      }
    })
  },

  // gets new document revision data
  async getDocumentDetailRevision(id, revId) {
    const documentDetailResponse = await handleApiError(() => documentApi.documentDetailRevision(
      id,
      revId
    ))

    return documentDetailResponse
  },
  async loadDocument(id, revId, shared = false) {
    this.loading = this.willChangeRevision ? this.loading : true
    this.documentRev = null
    filesToAVTest.clear()
    clearTimeout(checkAVInt)
    let documentDetailResponse

    if (shared) {
      documentDetailResponse = await handleApiError(() => documentApi.documentDetailShared(id))
      checkAVType = 'fileFilesCheckSharedDocument'
    
    } else if (revId === undefined) {
      documentDetailResponse = await handleApiError(() => documentApi.documentDetail(id))
      checkAVType = 'fileFilesCheckDocument'
    
    } else if (documentChangeRevisionCache?.id === id && documentChangeRevisionCache?.bc_document_rev_id === revId) {
      documentDetailResponse = {
        data: documentChangeRevisionCache
      }
      documentChangeRevisionCache = null
      checkAVType = 'fileFilesCheckDocumentRev'
    
    } else {
      documentDetailResponse = await this.getDocumentDetailRevision(id, revId)
      this.documentRev = revId
      checkAVType = 'fileFilesCheckDocumentRev'
    }

    // leave edit mode when document id change
    if (
      this.document?.id &&
      documentDetailResponse.data?.id !== this.document?.id &&
      this.willLoadInEdit === false
    ) {
      this.edit = false
    
    } else if (this.willLoadInEdit) {
      this.edit = true
    }

    if (this.willChangeRevision) {
      this.replaceLoadedDocumentDataWithNewRev(documentDetailResponse.data)
    
    } else {
      this.setDocument(documentDetailResponse.data)
    }

    // this.loading = false
    this.willLoadInEdit = false
    this.willChangeRevision = false
    //this.search = null
  },
  setWillChangeRevision(document) {
    this.willChangeRevision = true
    if (document) {
      documentChangeRevisionCache = document
    }
  },
  replaceLoadedDocumentDataWithNewRev(document) {
    const newDocumentElements = new Map()
    this.flattenDocumentRecursion(
      document.children,
      newDocumentElements,
      'from__bc_document_data_rev_id'
    )

    this.getFlattenDocument().forEach((element) => {
      if (!newDocumentElements.has(element.id)) {
        return
      }

      const newElement = newDocumentElements.get(element.id)

      // merge new element to old one
      // we have to keep keys: children, data, key?
      delete newElement.children
      delete newElement.data
      delete newElement.key
      assign(element, newElement)

      // delete deleted files from element
      if (element.children) {
        element.children = element.children.filter((child) => !child.file || !child.file.deleted)
      }
    })

    // merge new document to old one
    // we have to keep keys: children
    delete document.children
    assign(this.document, document)
    this.document.approval = document.approval
    resetFlatten(this.document)
  },
  async loadDocumentRevisions(id) {
    //this.loading = true
    const documentRevisionsResponse = await handleApiError(() => documentApi.documentListRevisions(
      id
    ))
    this.revisionsList = documentRevisionsResponse.data
    //this.loading = false
  },
  async saveElement(element, metaData, callback) {
    // does not work -> when creating new revision
    // const documentId = this.document.id
    // const documentRev = this.documentRev
    this.isNewDocument = false // reset new document
    return this.addToUpdateQueue(
      () => {
        // const element = { ...elementProxy }
        const data = { ...element.data, ...metaData }
        // drop products from page element data
        if (element.type === 'page' && data.products) {
          delete data.products
        }

        return new Promise((resolve, reject) => {
          // eslint-disable-next-line no-extra-semi
          ;(async () => {
            const elementSave = {
              parent__bc_document_data_rev_id:
                element.parent__bc_document_data_rev_id,
              type: element.type,
              data,
              rank: element.rank
            }

            let elementSaveResponse
            try {
              // console.time('saveElement api call')
              if (element.id) {
                elementSaveResponse = await handleApiError(() => documentApi.elementUpdate(
                  this.document.id,
                  this.documentRev,
                  element.id,
                  elementSave
                ))
              } else {
                elementSaveResponse = await handleApiError(() => documentApi.elementCreate(
                  this.document.id,
                  this.documentRev,
                  elementSave
                ))
              }
              // console.timeEnd('saveElement api call')
              
              console.time('saveElement render')
              if (element.root_rank !== elementSaveResponse.data.root_rank) {
                element.root_rank = elementSaveResponse.data.root_rank
                updateRootRanksRecursive(this.document.children)
              }

              if (typeof callback === 'function') {
                callback(elementSaveResponse.data, () => {
                    resolve(element)
                })
              } else {
                //element.data = elementSaveResponse.data.data
                //merge(element, elementSaveResponse.data)
                resolve(element)
              }
              console.timeEnd('saveElement render')
            } catch (error) {
              reject(error)
            }
            // console.timeEnd('saveElement')
          })()
        })
      }
    )
  },
  addToUpdateQueue(callback) {
    return promiseQueue.add(callback)
  },
  deleteElementsRecursive(elementsIds, children) {
    let documentRecursion = false
    if (children === undefined) {
      children = this.document.children
      documentRecursion = true
    }

    const toDeleteIndexes = []
    const toDeleteElementIdsIndexes = []
    elementsIds.forEach((id, index) => {
      const childIndex = findIndex(children, ['id', id])
      if (childIndex !== -1) {
        toDeleteIndexes.push(childIndex)
        toDeleteElementIdsIndexes.push(index)
        this.removeFromFlatten(children[childIndex])
      }
    })

    if (toDeleteIndexes.length) {
      toDeleteIndexes
        .sort()
        .reverse()
        .forEach((index) => {
          children.splice(index, 1)
        })

      toDeleteElementIdsIndexes
        .sort()
        .reverse()
        .forEach((index) => {
          elementsIds.splice(index, 1)
        })
    }
    
    let rank = 0
    children.forEach((child) => {
      child.rank = rank++
      if (child.children && child.children.length && elementsIds.length) {
        this.deleteElementsRecursive(elementsIds, child.children)
      }
    })

    if (documentRecursion) {
      updateRootRanksRecursive(this.document.children)
    }
  },
  async deleteElements(elements, callback) {
    return promiseQueue.add(
      () =>
        new Promise((resolve, reject) => {
          // eslint-disable-next-line no-extra-semi
          ;(async () => {
            try {
              const deleteResponse = await handleApiError(() => documentApi.elementMultipleDelete(
                this.document.id,
                this.document.bc_document_rev_id,
                {
                  ids: elements.map((item) => item.id)
                }
              ))

              if (typeof callback === 'function') {
                callback(deleteResponse.data, () => {
                  //element.data = elementSaveResponse.data.data
                  //merge(element, elementSaveResponse.data)
                  resolve(deleteResponse.data)
                })
              } else {
                //element.data = elementSaveResponse.data.data
                //merge(element, elementSaveResponse.data)
                resolve(deleteResponse.data)
              }
            } catch (error) {
              reject(error)
            }
          })()
        })
    )
  },
  findElementInDocument(elementId) {
    if (this.getFlattenDocumentByFromDataRevId().has(elementId)) {
      return this.getFlattenDocumentByFromDataRevId().get(elementId)
    
    } else if (this.getFlattenDocument().has(elementId)) {
      return this.getFlattenDocument().get(elementId)
    }

    return null
  },
  moveElementInDocument(element, toElement, index) {
    // find old parent element
    const oldParent = element.parent__bc_document_data_rev_id ? this.findElementInDocument(element.parent__bc_document_data_rev_id) : this.document
    
    // remove element from old parent children
    oldParent.children.splice(element.rank, 1)
    
    // rerank elements in oldParent
    if (oldParent.id !== toElement.id || oldParent.type !== toElement.type) {
      for (let rank = element.rank; rank < oldParent.children.length; rank++) {
        oldParent.children[rank].rank = rank
      }
    }

    if (element.type === 'header') { // header should eat elements bellow
      let nextHeaderIndex = toElement.children.length
      for (let checkIndex = index; checkIndex < toElement.children.length; checkIndex++) {
        if (toElement.children[checkIndex].type === 'header') {
          nextHeaderIndex = checkIndex
          break
        }
      }
      
      element.children.push(...toElement.children.splice(index, nextHeaderIndex - index))
      
      // rerank elements in header element
      for (let rank = 0; rank < element.children.length; rank++) {
        element.children[rank].rank = rank
        element.children[rank].parent__bc_document_data_rev_id = element.id
      }
    }

    // insert element to toElement
    toElement.children.splice(index, 0, element)
    element.parent__bc_document_data_rev_id = toElement.type !== 'document' ? toElement.id : null

    // rerank elements in toElement
    for (let rank = 0; rank < toElement.children.length; rank++) {
      toElement.children[rank].rank = rank
    }
  },
  async getAVResponse() {
    const fileInfoRequest = []
    const fileIdToIndex = new Map()
    const fileUploadIdToIndex = new Map()
    
    if (checkAVInt) {
      clearTimeout(checkAVInt)
    }

    filesToAVTest.forEach((value) => {
      if (value.file_id) {
        if (fileIdToIndex.has(value.file_id)) {
          fileInfoRequest[fileIdToIndex.get(value.file_id)].elementIds.push(value.elementId)
        } else {
          fileIdToIndex.set(value.file_id, fileInfoRequest.length)
          fileInfoRequest.push({
            file_id: value.file_id,
            elementIds: [value.elementId]
          })
        }
      } else if (value.bc_file_upload_id) {
        if (fileUploadIdToIndex.has(value.bc_file_upload_id)) {
          fileInfoRequest[fileUploadIdToIndex.get(value.bc_file_upload_id)].elementIds.push(value.elementId)
        } else {
          fileUploadIdToIndex.set(value.bc_file_upload_id, fileInfoRequest.length)
          fileInfoRequest.push({
            bc_file_upload_id: value.bc_file_upload_id,
            elementIds: [value.elementId]
          })
        }
      }
    })

    let checkResponse = null
    if (checkAVType === 'fileFilesCheckDocumentRev') {
      checkResponse = await handleApiError(() => documentApi.fileFilesCheckDocumentRev(this.document.id, this.documentRev, fileInfoRequest))
    
    } else {
      checkResponse = await handleApiError(() => documentApi[checkAVType](this.document.id, fileInfoRequest))
    }

    const processedElementsIds = []

    checkResponse.data.forEach((fileInfo) => {
      if (!filesToAVTest.has(fileInfo.element_id)) {
        return
      }

      const docElement = this.findElementInDocument(fileInfo.element_id)
      if (!docElement) {
        return
      }

      if (!docElement.file) {
        docElement.file = fileInfo
        if (docElement.file_upload) {
          docElement.file_upload = null
        }
        
        if (fileInfo.file_processed) {
          processedElementsIds.push(fileInfo.element_id)
        } else {
          filesToAVTest.set(fileInfo.element_id, {
            file_id: fileInfo.id,
            bc_file_upload_id: null,
            elementId: fileInfo.element_id,
          })
        }
        
        return
      }

      if (fileInfo.antivir_ok || fileInfo.antivir_tested) {
        docElement.file.antivir_ok = fileInfo.antivir_ok
        docElement.file.virus_found = fileInfo.virus_found
        docElement.file.antivir_tested = fileInfo.antivir_tested
        docElement.file.virus_base_version = fileInfo.virus_base_version
        docElement.file.antivir_skip = fileInfo.antivir_skip
        docElement.file.antivir_pending = fileInfo.antivir_pending
      }

      if (!fileInfo.antivir_ok) {
        return
      }

      const previewKeys = Object.keys(fileInfo.previews)
      previewKeys.forEach((previewKey) => {
        // do not replace video and audio link -> it will stop player if it is playing
        if (previewKey === 'video' && docElement.file?.previews.video) {
          return
        }

        if (previewKey === 'audio' && docElement.file?.previews.audio) {
          return
        }

        docElement.file.previews[previewKey] = fileInfo.previews[previewKey]
      })
      
      docElement.file.exif = fileInfo.exif
      docElement.file.tags = fileInfo.tags
      docElement.file.black_background = fileInfo.black_background

      if (fileInfo.file_processed) {
       processedElementsIds.push(fileInfo.element_id)
      }
    })

    processedElementsIds.forEach((elementId) => filesToAVTest.delete(elementId))
    if (filesToAVTest.size > 0) {
      checkAVInt = setTimeout(() => this.getAVResponse(), 5000)
    }
  },
  updateFilesToAntivirTest(elementId, fileId, fileUploadId) {
    if (filesToAVTest.has(elementId)) {
      return
    }

    // FileInfoRequest - ish
    filesToAVTest.set(elementId, {
      file_id: fileId,
      bc_file_upload_id: fileUploadId,
      elementId: elementId
    })

    if (checkAVInt) {
      clearTimeout(checkAVInt)
    }

    checkAVInt = setTimeout(() => this.getAVResponse(), 5000)
  }
}

export default defineStore({
  id: 'bcDocument',
  state,
  getters,
  actions
})
